import React, { useState, useEffect } from 'react';
import AmplifierList, { ManufacturerSearchSelect } from '../AmplifierScreen/AmplifierList';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import SetHomepageButton from '../SetHomepageButton/SetHomepageButton';
import './AmplifierSearchScreen.css';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });
const manufacturerApi = axios.create({ baseURL: API_URL });

const AmplifierSearchScreen = ({ onSelectAmp = null, aotdHistory = [] }) => {
  const pickerMode = !!onSelectAmp;
  const { isAdmin, token } = useAuth();
  const [searchText, setSearchText] = useState('');
  const [amplifiers, setAmplifiers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'list' or 'card'
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [filters, setFilters] = useState({
    manufacturer_id: '',
    year_from: '',
    year_to: '',
    model: '',
    serial_number: '',
    description: ''
  });

  // Pending filter state for card view (mirrors AmplifierList's pending pattern)
  const [pendingCardFilters, setPendingCardFilters] = useState({
    manufacturer_id: '',
    year_from: '',
    year_to: '',
    model: '',
    serial_number: ''
  });

  // Sync pending card filters when applied filters change
  useEffect(() => {
    setPendingCardFilters({
      manufacturer_id: filters.manufacturer_id || '',
      year_from: filters.year_from || '',
      year_to: filters.year_to || '',
      model: filters.model || '',
      serial_number: filters.serial_number || ''
    });
  }, [filters]);

  // Check if user is admin (isAdmin is a boolean, not a function)
  const userIsAdmin = isAdmin === true;

  // Build AOTD badge info for a given amp.
  // Returns { type: 'exact'|'mfr', label: string } or null.
  const getAotdBadge = (amp) => {
    if (!aotdHistory.length) return null;
    const exactMatch = aotdHistory.find(h => h.amplifier_id === amp.amplifier_id);
    if (exactMatch) {
      const days = Math.round((Date.now() - new Date(exactMatch.scheduled_date)) / 86400000);
      const when = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
      return { type: 'exact', label: `⚠️ Featured ${when}` };
    }
    const mfrMatch = aotdHistory.find(
      h => String(h.manufacturer_id) === String(amp.manufacturer_id)
    );
    if (mfrMatch) {
      const days = Math.round((Date.now() - new Date(mfrMatch.scheduled_date)) / 86400000);
      const when = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
      return { type: 'mfr', label: `${mfrMatch.manufacturer_name} featured ${when} (${mfrMatch.model || 'unknown model'})` };
    }
    return null;
  };

  useEffect(() => {
    fetchManufacturers();
  }, []);

  const applyCardFilter = (key) => {
    handleFilterChange(key, pendingCardFilters[key]);
  };

  // Render a filter input for card view — identical styling to list view's renderFilterInput
  const renderCardFilterInput = (filterKey, placeholder, type = 'text', customPadding = '6px 22px 6px 6px') => {
    const isFilterActive = filters[filterKey] && filters[filterKey].toString().trim() !== '';
    const hasUnappliedChanges = pendingCardFilters[filterKey] !== (filters[filterKey] || '');
    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <input
          type={type}
          placeholder={placeholder}
          value={pendingCardFilters[filterKey] || ''}
          onChange={e => setPendingCardFilters(prev => ({ ...prev, [filterKey]: e.target.value }))}
          onKeyPress={e => { if (e.key === 'Enter') applyCardFilter(filterKey); }}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: customPadding,
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '13px'
          }}
        />
        <button
          onClick={() => {
            if (isFilterActive && !hasUnappliedChanges) {
              setPendingCardFilters(prev => ({ ...prev, [filterKey]: '' }));
              handleFilterChange(filterKey, '');
            } else {
              applyCardFilter(filterKey);
            }
          }}
          title={isFilterActive && !hasUnappliedChanges ? 'Clear filter' : 'Apply filter (or press Enter)'}
          style={{
            position: 'absolute',
            right: '2px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: isFilterActive && !hasUnappliedChanges ? '#dc3545' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '2px',
            padding: '2px 5px',
            cursor: 'pointer',
            fontSize: '10px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            boxShadow: isFilterActive && !hasUnappliedChanges
              ? '0 1px 3px rgba(231, 76, 60, 0.3)'
              : '0 1px 3px rgba(37, 99, 235, 0.3)',
            height: 'calc(100% - 4px)',
            minWidth: '18px'
          }}
          onMouseEnter={(e) => {
            if (isFilterActive && !hasUnappliedChanges) {
              e.target.style.background = '#c0392b';
              e.target.style.boxShadow = '0 2px 4px rgba(231, 76, 60, 0.4)';
            } else {
              e.target.style.background = '#1d4ed8';
              e.target.style.boxShadow = '0 2px 4px rgba(37, 99, 235, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (isFilterActive && !hasUnappliedChanges) {
              e.target.style.background = '#dc3545';
              e.target.style.boxShadow = '0 1px 3px rgba(231, 76, 60, 0.3)';
            } else {
              e.target.style.background = '#2563eb';
              e.target.style.boxShadow = '0 1px 3px rgba(37, 99, 235, 0.3)';
            }
          }}
        >
          {isFilterActive && !hasUnappliedChanges ? '✕' : '✓'}
        </button>
      </div>
    );
  };

  const handleFilterChange = (key, value) => {
    // Support batch filter changes: if key is an object, merge all keys
    let newFilters;
    if (typeof key === 'object' && key !== null) {
      newFilters = { ...filters, ...key };
    } else {
      newFilters = { ...filters, [key]: value };
    }
    setFilters(newFilters);
    // Trigger search with new filters immediately, reset to page 1
    if (searchText.trim()) {
      handleSearchWithFilters(1, newFilters);
    }
  };

  // Filters are now applied on the backend, so use amplifiers directly
  const filteredAmplifiers = amplifiers;

  const activeFilterCount = [
    filters.manufacturer_id, filters.year_from, filters.year_to,
    filters.model, filters.serial_number,
  ].filter(Boolean).length;

  // Get manufacturers that appear in search results, sorted alphabetically
  const searchResultManufacturers = React.useMemo(() => {
    // Get unique manufacturer IDs from search results
    const manufacturerIds = new Set(amplifiers.map(amp => amp.manufacturer_id));
    
    // Filter manufacturers to only those in search results
    const filtered = manufacturers.filter(m => manufacturerIds.has(m.manufacturer_id));
    
    // Sort alphabetically by name
    return filtered.sort((a, b) => 
      (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
    );
  }, [amplifiers, manufacturers]);

  const fetchManufacturers = async () => {
    try {
      const res = await manufacturerApi.get('/manufacturers');
      const normalized = res.data.map(m => ({
        ...m,
        name: m.name || m.manufacturer_name
      }));
      setManufacturers(normalized);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
  };

  const handleSearchWithFilters = async (pageNum = 1, filtersToUse = filters) => {
    if (!searchText.trim()) {
      alert('Please enter search text');
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      // Build filter params - only include non-empty filters
      const filterParams = {};
      if (filtersToUse.manufacturer_id) filterParams.manufacturer_id = filtersToUse.manufacturer_id;
      if (filtersToUse.year_from) filterParams.year_from = filtersToUse.year_from;
      if (filtersToUse.year_to) filterParams.year_to = filtersToUse.year_to;
      if (filtersToUse.model) filterParams.model = filtersToUse.model;
      if (filtersToUse.serial_number) filterParams.serial_number = filtersToUse.serial_number;
      if (filtersToUse.description) filterParams.description = filtersToUse.description;
      
      // Call the search endpoint with search text, filters, and pagination
      const res = await api.get('/amplifiers/search', {
        params: { 
          q: searchText,
          page: pageNum,
          pageSize: pagination.pageSize,
          ...filterParams
        }
      });
      setAmplifiers(res.data.data || []);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error searching amplifiers:', error);
      alert('Error searching amplifiers: ' + error.message);
      setAmplifiers([]);
      setPagination({
        page: 1,
        pageSize: 50,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (pageNum = 1) => {
    // Clear column filters on new search
    const emptyFilters = {
      manufacturer_id: '',
      year_from: '',
      year_to: '',
      model: '',
      serial_number: '',
      description: ''
    };
    setFilters(emptyFilters);
    return handleSearchWithFilters(pageNum, emptyFilters);
  };

  const handlePageChange = (newPage) => {
    handleSearch(newPage);
  };

  const handleRandomManufacturer = async () => {
    if (manufacturers.length === 0) return;
    const rand = manufacturers[Math.floor(Math.random() * manufacturers.length)];
    setSearchText(rand.name);
    setLoading(true);
    setHasSearched(true);
    const emptyFilters = { manufacturer_id: '', year_from: '', year_to: '', model: '', serial_number: '', description: '' };
    setFilters(emptyFilters);
    try {
      const res = await api.get('/amplifiers/search', {
        params: { q: rand.name, page: 1, pageSize: pagination.pageSize }
      });
      setAmplifiers(res.data.data || []);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error fetching random manufacturer amps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchText('');
    setAmplifiers([]);
    setHasSearched(false);
  };

  const handleDelete = async (amplifierId) => {
    if (!userIsAdmin) {
      alert('Only administrators can delete amplifiers.');
      return;
    }
    
    const amplifierToDelete = amplifiers.find(a => a.amplifier_id === amplifierId);
    const confirmMessage = amplifierToDelete 
      ? `Are you sure you want to delete "${amplifierToDelete.name}"? This action cannot be undone.`
      : 'Are you sure you want to delete this amplifier? This action cannot be undone.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await api.delete(`/amplifiers/${amplifierId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Remove the deleted amplifier from the list
      setAmplifiers(prev => prev.filter(a => a.amplifier_id !== amplifierId));
    } catch (error) {
      console.error('Error deleting amplifier:', error);
      alert('Error deleting amplifier: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className={pickerMode ? '' : 'ass-outer'} style={pickerMode ? {} : {
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '2rem'
    }}>
      <div className={pickerMode ? '' : 'ass-inner'} style={pickerMode ? {} : {
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        {!pickerMode && (
          <div className="ass-header-card" style={{
            background: 'white',
            borderRadius: '10px',
            padding: '3rem',
            border: '1px solid #e9ecef',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <h1 style={{
              fontSize: '1.75rem',
              fontWeight: '600',
              color: '#333',
              letterSpacing: '-0.02em',
              margin: '0'
            }}>
              Amplifier Search
            </h1>
            <div style={{ marginTop: '8px' }}><SetHomepageButton pagePath="/amplifier_search" /></div>
          </div>
        )}
      
        <div className="ass-search-card" style={{ 
          background: 'white',
          borderRadius: '10px',
          padding: '2.5rem', 
          border: '1px solid #e9ecef',
          marginBottom: '2rem'
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '1rem', 
              fontWeight: '600',
              fontSize: '1rem',
              color: '#333',
              textAlign: 'center'
            }}>
              Search Amplifiers:
            </label>
            <input
              type="text"
              className="ass-search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Search by name, manufacturer, year range, model, or serial number..."
              style={{
                width: '100%',
                maxWidth: '600px',
                padding: '0.75rem 1.25rem',
                fontSize: '1rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: '#f8f9fa',
                color: '#333',
                margin: '0 auto',
                display: 'block',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                boxShadow: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
                e.target.style.boxShadow = 'none';
              }}
            />
            <style>
              {`
                input::placeholder {
                  color: rgba(100, 100, 100, 0.7);
                }
              `}
            </style>
          </div>
        
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => handleSearch()}
              disabled={loading || !searchText.trim()}
              style={{
                background: loading || !searchText.trim() ? 
                  '#ccc' : 
                  '#2563eb',
                color: 'white',
                border: 'none',
                padding: '0.55rem 1.25rem',
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '6px',
                cursor: loading || !searchText.trim() ? 'not-allowed' : 'pointer',
                marginRight: '1rem',
                transition: 'box-shadow 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading && searchText.trim()) {
                  e.target.style.background = '#1d4ed8';
                  e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && searchText.trim()) {
                  e.target.style.background = '#2563eb';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>

            {pickerMode && (
            <button
              onClick={handleRandomManufacturer}
              disabled={loading || manufacturers.length === 0}
              style={{
                background: loading || manufacturers.length === 0 ? '#ccc' : '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '0.55rem 1.25rem',
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '6px',
                cursor: loading || manufacturers.length === 0 ? 'not-allowed' : 'pointer',
                marginRight: '1rem',
                transition: 'box-shadow 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading && manufacturers.length > 0) {
                  e.target.style.background = '#6d28d9';
                  e.target.style.boxShadow = '0 2px 8px rgba(124, 58, 237, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && manufacturers.length > 0) {
                  e.target.style.background = '#7c3aed';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              🎲 Random Manufacturer
            </button>
            )}
            
            {hasSearched && (
              <button
                onClick={clearSearch}
                style={{
                  background: 'transparent',
                  color: '#888',
                  border: '1px solid #ddd',
                  padding: '0.55rem 1.25rem',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f8f9fa';
                  e.target.style.borderColor = '#ccc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.borderColor = '#ddd';
                }}
              >
                Clear
              </button>
            )}
          </div>
        
          <div style={{ 
            marginTop: '1.5rem', 
            fontSize: '0.85rem', 
            color: '#888',
            textAlign: 'center',
            padding: '0.85rem 1rem',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <strong>Search Tips:</strong> Search will look for matches in Manufacturer, Year Range, Model, and Serial Number
          </div>
        </div>

        {loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem',
            background: 'white',
          borderRadius: '10px',
            border: '1px solid #e9ecef',
            marginBottom: '2rem'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '1rem'
            }}>
              
            </div>
            <div style={{
              color: '#555',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}>
              Searching amplifiers...
            </div>
          </div>
        )}

        {hasSearched && !loading && (
          <div className="ass-count-card" style={{ 
            marginBottom: '2rem',
            background: 'white',
          borderRadius: '10px',
            padding: '1.5rem',
            border: '1px solid #e9ecef',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#333',
              fontSize: '1.05rem',
              fontWeight: '600',
              margin: '0 0 0.5rem 0'
            }}>
              Search Results
            </h3>
            <p style={{ 
              color: '#555',
              fontSize: '0.9rem',
              margin: '0'
            }}>
              Found <strong>{pagination.total}</strong> amplifier{pagination.total !== 1 ? 's' : ''} 
              {searchText && ` matching "${searchText}"`}
              {pagination.total > pagination.pageSize && (
                <span> (showing {amplifiers.length} on page {pagination.page} of {pagination.totalPages})</span>
              )}
            </p>
          </div>
        )}

        {hasSearched && !loading && (
          <div className="ass-results-card" style={{
            background: 'white',
          borderRadius: '10px',
            padding: '2rem',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <div className="ass-results-header" style={{
              marginBottom: '1.25rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                margin: '0',
                color: '#333',
                fontSize: '1.25rem',
                fontWeight: '600',
                letterSpacing: '-0.02em',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                Search Results
                <span style={{
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  color: '#2563eb',
                  background: 'rgba(37, 99, 235, 0.1)',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '8px'
                }}>
                  {filteredAmplifiers.length} of {pagination.total} found
                </span>
              </h2>
              {/* Mobile filter button — hidden on desktop via CSS */}
              <button className="ass-mobile-filter-btn" onClick={() => setShowMobileFilters(true)} aria-label="Open filters">
                ⚙ Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
              </button>
              {/* List / Card toggle */}
              <div style={{ display: 'flex', gap: '2px', background: '#f1f3f5', borderRadius: '6px', padding: '2px' }}>
                <button
                  onClick={() => setViewMode('list')}
                  title="List view"
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    background: viewMode === 'list' ? '#2563eb' : 'transparent',
                    color: viewMode === 'list' ? 'white' : '#666',
                    transition: 'all 0.15s ease'
                  }}
                >
                  ☰ List
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  title="Card view"
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    background: viewMode === 'card' ? '#2563eb' : 'transparent',
                    color: viewMode === 'card' ? 'white' : '#666',
                    transition: 'all 0.15s ease'
                  }}
                >
                  ▦ Cards
                </button>
              </div>
            </div>

            {viewMode === 'card' ? (
              /* ── Card View ── */
              <>
              {/* Card View Filter Bar — matches list view exactly; hidden on mobile (drawer used instead) */}
              <div className="ass-filter-bar" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                alignItems: 'flex-end'
              }}>
                {/* Manufacturer — same ManufacturerSearchSelect as list view */}
                <div style={{ minWidth: '160px', flex: '1 1 180px', maxWidth: '240px' }}>
                  <ManufacturerSearchSelect
                    manufacturers={searchResultManufacturers}
                    value={filters.manufacturer_id || ''}
                    pendingValue={pendingCardFilters.manufacturer_id || ''}
                    onPendingChange={val => setPendingCardFilters(prev => ({ ...prev, manufacturer_id: val }))}
                    onApply={val => {
                      const v = val !== undefined ? val : pendingCardFilters.manufacturer_id;
                      setPendingCardFilters(prev => ({ ...prev, manufacturer_id: v }));
                      handleFilterChange('manufacturer_id', v);
                    }}
                    onClear={() => {
                      setPendingCardFilters(prev => ({ ...prev, manufacturer_id: '' }));
                      handleFilterChange('manufacturer_id', '');
                    }}
                  />
                </div>
                {/* Year Range — same YYYY mask + combined apply/clear as list view */}
                <div style={{ minWidth: '120px', flex: '0 1 140px' }}>
                  {(() => {
                    const isYearActive = (filters.year_from && filters.year_from.toString().trim() !== '') || (filters.year_to && filters.year_to.toString().trim() !== '');
                    const hasUnapplied = pendingCardFilters.year_from !== (filters.year_from || '') || pendingCardFilters.year_to !== (filters.year_to || '');
                    return (
                      <div style={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="YYYY"
                          maxLength={4}
                          value={pendingCardFilters.year_from || ''}
                          onChange={e => {
                            const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                            setPendingCardFilters(prev => ({ ...prev, year_from: val }));
                          }}
                          onKeyPress={e => {
                            if (!/[0-9]/.test(e.key)) e.preventDefault();
                            if (e.key === 'Enter') {
                              handleFilterChange({ year_from: pendingCardFilters.year_from, year_to: pendingCardFilters.year_to });
                            }
                          }}
                          style={{
                            width: '50%',
                            minWidth: '38px',
                            boxSizing: 'border-box',
                            padding: '6px 3px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '13px',
                            textAlign: 'center'
                          }}
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="YYYY"
                          maxLength={4}
                          value={pendingCardFilters.year_to || ''}
                          onChange={e => {
                            const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                            setPendingCardFilters(prev => ({ ...prev, year_to: val }));
                          }}
                          onKeyPress={e => {
                            if (!/[0-9]/.test(e.key)) e.preventDefault();
                            if (e.key === 'Enter') {
                              handleFilterChange({ year_from: pendingCardFilters.year_from, year_to: pendingCardFilters.year_to });
                            }
                          }}
                          style={{
                            width: '50%',
                            minWidth: '38px',
                            boxSizing: 'border-box',
                            padding: '6px 3px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '13px',
                            textAlign: 'center'
                          }}
                        />
                        <button
                          onClick={() => {
                            if (isYearActive && !hasUnapplied) {
                              setPendingCardFilters(prev => ({ ...prev, year_from: '', year_to: '' }));
                              handleFilterChange({ year_from: '', year_to: '' });
                            } else {
                              handleFilterChange({ year_from: pendingCardFilters.year_from, year_to: pendingCardFilters.year_to });
                            }
                          }}
                          title={isYearActive && !hasUnapplied ? 'Clear year filter' : 'Apply year filter (or press Enter)'}
                          style={{
                            background: isYearActive && !hasUnapplied ? '#dc3545' : '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '2px',
                            padding: '2px 5px',
                            cursor: 'pointer',
                            fontSize: '10px',
                            fontWeight: '600',
                            height: '26px',
                            minWidth: '18px',
                            transition: 'all 0.2s ease',
                            boxShadow: isYearActive && !hasUnapplied
                              ? '0 1px 3px rgba(231, 76, 60, 0.3)'
                              : '0 1px 3px rgba(37, 99, 235, 0.3)'
                          }}
                        >{isYearActive && !hasUnapplied ? '✕' : '✓'}</button>
                      </div>
                    );
                  })()}
                </div>
                {/* Model — same renderFilterInput style as list view */}
                <div style={{ minWidth: '120px', flex: '1 1 160px', maxWidth: '200px' }}>
                  {renderCardFilterInput('model', 'Model')}
                </div>
                {/* Serial # — same renderFilterInput style as list view */}
                <div style={{ minWidth: '100px', flex: '0 1 130px' }}>
                  {renderCardFilterInput('serial_number', 'Serial #', 'text', '6px 22px 6px 4px')}
                </div>
              </div>
              <div className="ass-card-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '1.25rem'
              }}>
                {filteredAmplifiers.map(amp => {
                  const detailUrl = amp.manufacturer_slug && amp.slug
                    ? `/amps/${amp.manufacturer_slug}/${amp.slug}`
                    : `/amplifier-components/${amp.amplifier_id}`;
                  const thumbUrl = amp.thumbnail_artifact_id
                    ? `${API_URL}/amplifier_artifacts/${amp.thumbnail_artifact_id}/thumbnail`
                    : null;
                  const yearStr = amp.start_year
                    ? (amp.end_year && amp.end_year !== amp.start_year ? `${amp.start_year}–${amp.end_year}` : `${amp.start_year}`)
                    : '';
                  return (
                    <div
                      key={amp.amplifier_id}
                      onClick={() => window.open(detailUrl, '_blank')}
                      style={{
                        background: 'white',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,99,235,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'none'; }}
                    >
                      {/* Thumbnail */}
                      <div style={{
                        width: '100%',
                        aspectRatio: '4 / 3',
                        background: '#f1f3f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}>
                        {thumbUrl ? (
                          <img
                            src={thumbUrl}
                            alt={amp.model || amp.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            loading="lazy"
                          />
                        ) : (
                          <span style={{ fontSize: '2.5rem', color: '#ccc' }}>🎸</span>
                        )}
                      </div>
                      {/* Info */}
                      <div style={{ padding: '0.75rem' }}>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: '500', marginBottom: '0.2rem' }}>
                          {amp.manufacturer_name}
                        </div>
                        {pickerMode ? (
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1e293b', lineHeight: '1.3' }}>
                            {amp.model || amp.name}
                          </div>
                        ) : (
                          <a
                            href={detailUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#1e293b', lineHeight: '1.3', textDecoration: 'none' }}
                          >
                            {amp.model || amp.name}
                          </a>
                        )}
                        {yearStr && (
                          <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                            {yearStr}
                          </div>
                        )}
                        {pickerMode && (() => {
                          const badge = getAotdBadge(amp);
                          return badge ? (
                            <div style={{
                              marginTop: '0.35rem',
                              fontSize: '0.72rem',
                              fontWeight: '600',
                              color: badge.type === 'exact' ? '#92400e' : '#78350f',
                              background: badge.type === 'exact' ? '#fef3c7' : '#fef9c3',
                              border: `1px solid ${badge.type === 'exact' ? '#fcd34d' : '#fde68a'}`,
                              borderRadius: '4px',
                              padding: '2px 6px',
                              lineHeight: '1.4'
                            }}>
                              {badge.label}
                            </div>
                          ) : null;
                        })()}
                        {pickerMode ? (
                          <button
                            onClick={e => { e.stopPropagation(); onSelectAmp(amp); }}
                            style={{
                              marginTop: '0.5rem',
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '0.3rem 0.75rem',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              transition: 'background 0.15s ease'
                            }}
                            onMouseEnter={e => e.target.style.background = '#218838'}
                            onMouseLeave={e => e.target.style.background = '#28a745'}
                          >
                            ✓ Select
                          </button>
                        ) : userIsAdmin && (
                          <button
                            onClick={e => { e.stopPropagation(); handleDelete(amp.amplifier_id); }}
                            title="Delete amplifier"
                            style={{
                              marginTop: '0.5rem',
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '0.25rem 0.5rem',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              transition: 'background 0.15s ease'
                            }}
                            onMouseEnter={e => e.target.style.background = '#c0392b'}
                            onMouseLeave={e => e.target.style.background = '#dc3545'}
                          >
                            🗑 Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            ) : (
              /* ── List View (existing) ── */
              <AmplifierList
              amplifiers={filteredAmplifiers}
              filters={filters}
              onFilterChange={handleFilterChange}
              manufacturers={searchResultManufacturers}
              onEdit={null} // No editing from search screen
              onDelete={userIsAdmin ? handleDelete : null}
              renderExtraActions={pickerMode
                ? (amp) => {
                    const badge = getAotdBadge(amp);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        {badge && (
                          <div style={{
                            fontSize: '0.72rem',
                            fontWeight: '600',
                            color: badge.type === 'exact' ? '#92400e' : '#78350f',
                            background: badge.type === 'exact' ? '#fef3c7' : '#fef9c3',
                            border: `1px solid ${badge.type === 'exact' ? '#fcd34d' : '#fde68a'}`,
                            borderRadius: '4px',
                            padding: '2px 6px',
                          }}>
                            {badge.label}
                          </div>
                        )}
                        <button
                          onClick={() => onSelectAmp(amp)}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 0.85rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            transition: 'box-shadow 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#218838';
                            e.target.style.boxShadow = '0 2px 8px rgba(40, 167, 69, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#28a745';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          ✓ Select
                        </button>
                      </div>
                    );
                  }
                : (amp) => (
                    <a
                      href={amp.manufacturer_slug && amp.slug ? `/amps/${amp.manufacturer_slug}/${amp.slug}` : `/amplifier-components/${amp.amplifier_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: '#2563eb',
                        color: 'white',
                        borderRadius: '6px',
                        padding: '0.4rem 0.85rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        textDecoration: 'none',
                        transition: 'box-shadow 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#1d4ed8';
                        e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#2563eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      View Details
                    </a>
                  )
              }
              hideDescription={true}
              readOnly={!userIsAdmin} // Admins can delete from search results
            />
            )}
            
            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={!pagination.hasPrev}
                  style={{
                    padding: '0.4rem 0.85rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    background: !pagination.hasPrev ? '#f5f5f5' : 'white',
                    color: !pagination.hasPrev ? '#bbb' : '#333',
                    cursor: !pagination.hasPrev ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '0.82rem',
                    transition: 'box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (pagination.hasPrev) {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.color = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = pagination.hasPrev ? '#333' : '#bbb';
                  }}
                >
                  First
                </button>
                
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev}
                  style={{
                    padding: '0.4rem 0.85rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    background: !pagination.hasPrev ? '#f5f5f5' : 'white',
                    color: !pagination.hasPrev ? '#bbb' : '#333',
                    cursor: !pagination.hasPrev ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '0.82rem',
                    transition: 'box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (pagination.hasPrev) {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.color = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = pagination.hasPrev ? '#333' : '#bbb';
                  }}
                >
                  Previous
                </button>
                
                <div style={{
                  padding: '0.4rem 1rem',
                  borderRadius: '6px',
                  background: 'rgba(37, 99, 235, 0.08)',
                  color: '#2563eb',
                  fontWeight: '500',
                  fontSize: '0.82rem'
                }}>
                  Page {pagination.page} of {pagination.totalPages}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                  style={{
                    padding: '0.4rem 0.85rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    background: !pagination.hasNext ? '#f5f5f5' : 'white',
                    color: !pagination.hasNext ? '#bbb' : '#333',
                    cursor: !pagination.hasNext ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '0.82rem',
                    transition: 'box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (pagination.hasNext) {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.color = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = pagination.hasNext ? '#333' : '#bbb';
                  }}
                >
                  Next
                </button>
                
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={!pagination.hasNext}
                  style={{
                    padding: '0.4rem 0.85rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    background: !pagination.hasNext ? '#f5f5f5' : 'white',
                    color: !pagination.hasNext ? '#bbb' : '#333',
                    cursor: !pagination.hasNext ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '0.82rem',
                    transition: 'box-shadow 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (pagination.hasNext) {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.color = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = pagination.hasNext ? '#333' : '#bbb';
                  }}
                >
                  Last
                </button>
              </div>
            )}
          </div>
        )}
      {/* Mobile filter drawer */}
      {showMobileFilters && (
        <div className="ass-filter-overlay" onClick={() => setShowMobileFilters(false)}>
          <div className="ass-filter-drawer" onClick={e => e.stopPropagation()}>
            <div className="ass-filter-drawer-header">
              <span>Refine Results</span>
              <button className="ass-filter-drawer-close" onClick={() => setShowMobileFilters(false)} aria-label="Close filters">✕</button>
            </div>
            <div className="ass-filter-drawer-body">
              <div className="ass-filter-drawer-row">
                <label className="ass-filter-drawer-label">Manufacturer</label>
                <ManufacturerSearchSelect
                  manufacturers={searchResultManufacturers}
                  value={filters.manufacturer_id || ''}
                  pendingValue={pendingCardFilters.manufacturer_id || ''}
                  onPendingChange={val => setPendingCardFilters(prev => ({ ...prev, manufacturer_id: val }))}
                  onApply={val => {
                    const v = val !== undefined ? val : pendingCardFilters.manufacturer_id;
                    setPendingCardFilters(prev => ({ ...prev, manufacturer_id: v }));
                    handleFilterChange('manufacturer_id', v);
                  }}
                  onClear={() => {
                    setPendingCardFilters(prev => ({ ...prev, manufacturer_id: '' }));
                    handleFilterChange('manufacturer_id', '');
                  }}
                />
              </div>
              <div className="ass-filter-drawer-row">
                <label className="ass-filter-drawer-label">Year Range</label>
                {(() => {
                  const isYearActive = (filters.year_from && filters.year_from.toString().trim() !== '') || (filters.year_to && filters.year_to.toString().trim() !== '');
                  const hasUnapplied = pendingCardFilters.year_from !== (filters.year_from || '') || pendingCardFilters.year_to !== (filters.year_to || '');
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="From YYYY" maxLength={4}
                        value={pendingCardFilters.year_from || ''}
                        onChange={e => { const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4); setPendingCardFilters(prev => ({ ...prev, year_from: v })); }}
                        onKeyPress={e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); if (e.key === 'Enter') handleFilterChange({ year_from: pendingCardFilters.year_from, year_to: pendingCardFilters.year_to }); }}
                        style={{ flex: 1, boxSizing: 'border-box', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', textAlign: 'center' }}
                      />
                      <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>–</span>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="To YYYY" maxLength={4}
                        value={pendingCardFilters.year_to || ''}
                        onChange={e => { const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4); setPendingCardFilters(prev => ({ ...prev, year_to: v })); }}
                        onKeyPress={e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); if (e.key === 'Enter') handleFilterChange({ year_from: pendingCardFilters.year_from, year_to: pendingCardFilters.year_to }); }}
                        style={{ flex: 1, boxSizing: 'border-box', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', textAlign: 'center' }}
                      />
                      <button
                        onClick={() => {
                          if (isYearActive && !hasUnapplied) {
                            setPendingCardFilters(prev => ({ ...prev, year_from: '', year_to: '' }));
                            handleFilterChange({ year_from: '', year_to: '' });
                          } else {
                            handleFilterChange({ year_from: pendingCardFilters.year_from, year_to: pendingCardFilters.year_to });
                          }
                        }}
                        style={{ background: isYearActive && !hasUnapplied ? '#dc3545' : '#2563eb', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                      >{isYearActive && !hasUnapplied ? '✕' : '✓'}</button>
                    </div>
                  );
                })()}
              </div>
              <div className="ass-filter-drawer-row">
                <label className="ass-filter-drawer-label">Model</label>
                {renderCardFilterInput('model', 'Model')}
              </div>
              <div className="ass-filter-drawer-row">
                <label className="ass-filter-drawer-label">Serial #</label>
                {renderCardFilterInput('serial_number', 'Serial #', 'text', '6px 22px 6px 4px')}
              </div>
            </div>
            <div className="ass-filter-drawer-footer">
              {activeFilterCount > 0 && (
                <button className="ass-filter-clear-btn" onClick={() => {
                  const empty = { manufacturer_id: '', year_from: '', year_to: '', model: '', serial_number: '' };
                  setPendingCardFilters(empty);
                  handleFilterChange(empty);
                }}>
                  Clear all ({activeFilterCount})
                </button>
              )}
              <button className="ass-filter-done-btn" onClick={() => setShowMobileFilters(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AmplifierSearchScreen;
